import { listGames, updateGame, deleteGame } from "./games.js";
import { allTags } from "../utils/tags.js";
import { setTagFacets, setAiringFacets } from "../utils/filters.js";
import { isAiringCatalog } from "../utils/seasons.js";
import { todayDate } from "../utils/dates.js";
import { listBooks, updateBook, deleteBook } from "./books.js";
import { listTvSeries, updateTvSeries, deleteTvSeries } from "./tv.js";
import { listAnime, updateAnime, deleteAnime } from "./anime.js";
import { listMovies, updateMovie, deleteMovie } from "./movies.js";
import { listComics, updateComic, deleteComic } from "./comics.js";
import { getDB } from "./schema.js";
import { queueCloudWrite } from "./sync.js";
import { forgetLastRemoval } from "./history.js";
import { comicFormatShort } from "../utils/comics.js";

// Normalizes games and books into one shape so cross-media views (Most wanted,
// Release schedule, Groups) can list them together. `route` points at the right
// detail page; `secondary` is a subtitle (year for games, authors for books).
// `rating` / `franchise` / `collection` / `authors` are carried so groups can
// summarize members and resolve auto-rules across media.

function normalizeGame(g) {
	return {
		id: g.id,
		media_type: "videogame",
		mediaLabel: "Game",
		title: g.title,
		cover_url: g.cover_url ?? null,
		ownership_status: g.ownership_status ?? null,
		engagement_status: g.engagement_status ?? null,
		rating: g.rating ?? null,
		awards: g.awards ?? [],
		release_year: g.release_year ?? null,
		release_date: g.release_date ?? null,
		release_precision: g.release_precision ?? null,
		franchise: g.franchise ?? null,
		collection: g.collection ?? null,
		authors: [],
		secondary: g.release_year ? String(g.release_year) : "",
		created_at: g.created_at ?? "",
		route: `/games/${g.id}`,
	};
}

function normalizeBook(b) {
	return {
		id: b.id,
		media_type: "book",
		mediaLabel: "Book",
		title: b.title,
		// A chosen edition cover overrides the source cover for display.
		cover_url: b.cover_override_url ?? b.cover_url ?? null,
		ownership_status: b.ownership_status ?? null,
		engagement_status: b.engagement_status ?? null,
		rating: b.rating ?? null,
		awards: b.awards ?? [],
		// Books only carry a publish year, not a precise date.
		release_year: b.published_year ?? null,
		release_date: null,
		franchise: null,
		collection: null,
		authors: b.authors ?? [],
		secondary: (b.authors ?? []).join(", "),
		created_at: b.created_at ?? "",
		route: `/books/${b.id}`,
	};
}

// TV series sit alongside games/books in cross-media views. Seasons/episodes are
// sub-items and stay out of this list.
function normalizeTv(t) {
	return {
		id: t.id,
		media_type: "tv",
		mediaLabel: "TV",
		title: t.title,
		cover_url: t.cover_url ?? null,
		ownership_status: t.ownership_status ?? null,
		engagement_status: t.engagement_status ?? null,
		rating: t.rating ?? null,
		awards: t.awards ?? [],
		release_year: t.premiered_year ?? null,
		release_date: null,
		franchise: null,
		collection: null,
		authors: [],
		secondary: t.premiered_year ? String(t.premiered_year) : "",
		created_at: t.created_at ?? "",
		route: `/tv/${t.id}`,
	};
}

// Anime entries sit alongside the others. One row per series (TVmaze-backed, like
// TV); seasons and episodes are sub-items and stay out of this list.
function normalizeAnime(a) {
	return {
		id: a.id,
		media_type: "anime",
		mediaLabel: "Anime",
		title: a.title,
		cover_url: a.cover_url ?? null,
		ownership_status: a.ownership_status ?? null,
		engagement_status: a.engagement_status ?? null,
		rating: a.rating ?? null,
		awards: a.awards ?? [],
		release_year: a.premiered_year ?? null,
		release_date: null,
		franchise: null,
		collection: null,
		authors: [],
		secondary: a.premiered_year ? String(a.premiered_year) : "",
		created_at: a.created_at ?? "",
		route: `/anime/${a.id}`,
	};
}

// Movies are flat like games and, like games, carry a PRECISE release date —
// that is what puts them on the Release schedule and in the bell scan.
function normalizeMovie(m) {
	return {
		id: m.id,
		media_type: "movie",
		mediaLabel: "Movie",
		title: m.title,
		cover_url: m.cover_url ?? null,
		ownership_status: m.ownership_status ?? null,
		engagement_status: m.engagement_status ?? null,
		rating: m.rating ?? null,
		awards: m.awards ?? [],
		release_year: m.release_year ?? null,
		release_date: m.release_date ?? null,
		franchise: null,
		collection: null,
		authors: [],
		secondary: m.release_year ? String(m.release_year) : "",
		created_at: m.created_at ?? "",
		route: `/movies/${m.id}`,
	};
}

// Comics are flat; the subtitle says what kind of thing it is (Issue / TPB / HC).
function normalizeComic(c) {
	return {
		id: c.id,
		media_type: "comic",
		mediaLabel: "Comic",
		title: c.title,
		cover_url: c.cover_url ?? null,
		ownership_status: c.ownership_status ?? null,
		engagement_status: c.engagement_status ?? null,
		rating: c.rating ?? null,
		awards: c.awards ?? [],
		release_year: c.release_year ?? null,
		release_date: null,
		franchise: null,
		collection: null,
		authors: [],
		secondary: [comicFormatShort(c.format), c.release_year].filter(Boolean).join(" · "),
		created_at: c.created_at ?? "",
		route: `/comics/${c.id}`,
	};
}

// All tracked items across every media type, in one normalized list.
export async function listAllMedia() {
	const [games, books, tv, anime, movies, comics] = await Promise.all([
		listGames(),
		listBooks(),
		listTvSeries(),
		listAnime(),
		listMovies(),
		listComics(),
	]);
	return [
		...games.map(withUserFields(normalizeGame)),
		...books.map(withUserFields(normalizeBook)),
		...tv.map(withUserFields(normalizeTv)),
		...anime.map(withUserFields(normalizeAnime)),
		...movies.map(withUserFields(normalizeMovie)),
		...comics.map(withUserFields(normalizeComic)),
	];
}

// The user's own fields every cross-media screen may show (tags, progress,
// loans, price notes), carried over from the raw record.
function withUserFields(normalize) {
	return (raw) => ({
		...normalize(raw),
		tags: raw.tags ?? [],
		// What the list filters match on, so smart collections work cross-media.
		genres: raw.genres ?? [],
		owned_on: raw.owned_on ?? [],
		source_values: raw.source_values ?? undefined,
		igdb_id: raw.igdb_id,
		imdb_id: raw.imdb_id,
		tvmaze_id: raw.tvmaze_id,
		ol_id: raw.ol_id,
		mal_id: raw.mal_id,
		progress: raw.progress ?? null,
		loan: raw.loan ?? null,
		price_note: raw.price_note ?? null,
	});
}

// Update an engagement status on a normalized item, routed to the right store.
export function updateMediaStatus(item, engagement_status) {
	if (item.media_type === "book") return updateBook(item.id, { engagement_status });
	if (item.media_type === "tv")
		return updateTvSeries(item.id, { engagement_status });
	if (item.media_type === "anime")
		return updateAnime(item.id, { engagement_status });
	if (item.media_type === "movie")
		return updateMovie(item.id, { engagement_status });
	if (item.media_type === "comic")
		return updateComic(item.id, { engagement_status });
	return updateGame(item.id, { engagement_status });
}

// ---------- any item, by media type (bulk actions, undo) ----------

export const STORE_BY_MEDIA = {
	videogame: "games",
	book: "books",
	tv: "tv",
	anime: "anime",
	movie: "movies",
	comic: "comics",
};
const UPDATE_BY_MEDIA = {
	videogame: updateGame,
	book: updateBook,
	tv: updateTvSeries,
	anime: updateAnime,
	movie: updateMovie,
	comic: updateComic,
};
const DELETE_BY_MEDIA = {
	videogame: deleteGame,
	book: deleteBook,
	tv: deleteTvSeries,
	anime: deleteAnime,
	movie: deleteMovie,
	comic: deleteComic,
};

// Refresh the TV / anime "On the air" facet from the local episode catalogs.
export async function loadAiringFacets(today = todayDate()) {
	const db = await getDB();
	const now = [];
	const running = [];
	for (const store of ["tv_catalog", "anime_catalog"]) {
		for (const c of await db.getAll(store)) {
			if (isAiringCatalog(c, today)) now.push(c.tvmaze_id);
			if (c.status === "Running") running.push(c.tvmaze_id);
		}
	}
	setAiringFacets({ now, running });
}

// Refresh the list filters' "Your tags" options (utils/filters.js).
export async function loadTagFacets() {
	const db = await getDB();
	const byMedia = {};
	for (const [media, store] of Object.entries(STORE_BY_MEDIA)) byMedia[media] = allTags(await db.getAll(store));
	setTagFacets(byMedia);
}

// The full stored record behind a normalized item (or an id + media type).
export async function getMediaRecord(mediaType, id) {
	const db = await getDB();
	return db.get(STORE_BY_MEDIA[mediaType], id);
}

// Patch any item through its own update function (history + sync included).
export function updateMediaItem(mediaType, id, patch) {
	const update = UPDATE_BY_MEDIA[mediaType];
	if (!update) throw new Error(`Unknown media type ${mediaType}`);
	return update(id, patch);
}

// Delete any item; resolves the record as it was, for an Undo.
export async function deleteMediaItem(mediaType, id) {
	const record = await getMediaRecord(mediaType, id);
	if (!record) return null;
	await DELETE_BY_MEDIA[mediaType](id);
	return record;
}

// Undo a delete: put the record back (same id, so groups/rankings/history still
// point at it), push it to the cloud again, and drop the "Removed" entry from
// History. The TV/anime episode catalog is re-fetched when the detail opens.
export async function restoreMediaItem(mediaType, record) {
	const store = STORE_BY_MEDIA[mediaType];
	if (!store || !record?.id) throw new Error("Nothing to restore");
	const db = await getDB();
	const { _synced, ...clean } = record;
	await db.put(store, clean);
	await queueCloudWrite({ type: "create", mediaType, record: clean });
	await forgetLastRemoval(record.id);
	return clean;
}
